import Layout from '@/components/feature/Layout';
import { companyInfo } from '@/mocks/company';
import { products } from '@/mocks/products';
import { Link } from 'react-router-dom';

const whatsappMessage = encodeURIComponent('Olá! Estou no site da Maquinasdesoldageomembrana.com.br e gostaria de informações sobre máquinas de solda de geomembrana.');
const whatsappUrl = `https://wa.me/${companyInfo.whatsapp}?text=${whatsappMessage}`;

const schema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Máquina de Solda Geomembrana Automática',
  description: 'Máquinas de solda geomembrana com cunha quente automática para grandes áreas.',
  url: 'https://maquinadesoldageomembrana.com.br/maquina-de-solda-geomembrana-automatica',
  inLanguage: 'pt-BR',
};

export default function AutomaticaPage() {
  const automaticas = products.filter((p) => p.category === 'Cunha Quente');

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      <div className="w-full px-4 md:px-6 lg:px-10 pt-28 md:pt-32 pb-16 md:pb-24">
        <nav className="text-xs text-foreground-400 mb-6">
          <Link to="/" className="hover:text-primary-600 cursor-pointer">Home</Link>
          <i className="ri-arrow-right-s-line mx-1" />
          <Link to="/maquina-de-solda-de-geomembrana" className="hover:text-primary-600 cursor-pointer">Máquinas</Link>
          <i className="ri-arrow-right-s-line mx-1" />
          <span className="text-foreground-600">Automática</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-12">
          <div className="lg:col-span-2">
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 uppercase leading-tight mb-6">
              Máquina de Solda Geomembrana Automática
            </h1>

            <div className="prose prose-sm max-w-none text-foreground-600 leading-relaxed space-y-4">
              <p>
                As <strong className="text-foreground-950">máquinas de solda de geomembrana automáticas</strong> com cunha quente oferecem tração motorizada para soldagem contínua em grandes áreas. Equipamentos como a <strong>BT950</strong> e <strong>CX500L</strong> avançam automaticamente, mantendo velocidade e temperatura uniformes, reduzindo erro humano e aumentando a produtividade.
              </p>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">Vantagens da Solda Automática</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: 'ri-speed-line', title: 'Maior Produtividade', desc: 'Até 800 m²/h em áreas planas, vs. 100-150 m²/h manual.' },
                  { icon: 'ri-medal-line', title: 'Qualidade Consistente', desc: 'Temperatura e velocidade fixas eliminam variabilidade do operador.' },
                  { icon: 'ri-award-line', title: 'Solda Dupla com Canal', desc: 'Permite teste de pressão conforme norma NBR 16199.' },
                  { icon: 'ri-user-heart-line', title: 'Menor Fadiga', desc: 'Operador apenas acompanha o equipamento.' },
                ].map((item) => (
                  <div key={item.title} className="bg-background-100 rounded-xl border border-background-200/70 p-5">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500/10 mb-3">
                      <i className={`${item.icon} text-primary-500 text-lg`} />
                    </div>
                    <h3 className="text-sm font-bold text-foreground-900 mb-1">{item.title}</h3>
                    <p className="text-foreground-500 text-xs">{item.desc}</p>
                  </div>
                ))}
              </div>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">Quando Usar Automática</h2>
              <ul className="space-y-2">
                <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" /><span><strong>Aterros sanitários</strong> — grandes áreas planas com milhares de m².</span></li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" /><span><strong>Barragens e reservatórios</strong> — extensões longas de linha reta.</span></li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" /><span><strong>Mineração</strong> — tanques de lixiviados e pilhas de rejeito.</span></li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" /><span><strong>Projetos com prazo curto</strong> — produtividade é crítica.</span></li>
              </ul>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">Limitações</h2>
              <ul className="space-y-2">
                <li className="flex items-start gap-2"><i className="ri-close-line text-foreground-300 mt-0.5 flex-shrink-0" /><span>Requer área plana ou declive suave para tração estável.</span></li>
                <li className="flex items-start gap-2"><i className="ri-close-line text-foreground-300 mt-0.5 flex-shrink-0" /><span>Não realiza cantos, emendas complexas ou reparos (use extrusora).</span></li>
                <li className="flex items-start gap-2"><i className="ri-close-line text-foreground-300 mt-0.5 flex-shrink-0" /><span>Investimento maior que máquinas manuais.</span></li>
              </ul>
            </div>

            <div className="mt-8 flex flex-wrap gap-2">
              <Link to="/modelos-de-maquinas" className="text-sm text-primary-600 hover:text-primary-700 font-medium bg-primary-50 px-3 py-1.5 rounded-full cursor-pointer">Ver Comparativo Completo</Link>
              <Link to="/maquina-de-solda-geomembrana-manual" className="text-sm text-primary-600 hover:text-primary-700 font-medium bg-primary-50 px-3 py-1.5 rounded-full cursor-pointer">Ver Modelos Manuais</Link>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-primary-50 rounded-2xl border border-primary-200/70 p-6">
              <h3 className="font-heading text-lg font-bold text-foreground-950 uppercase mb-3">Solicitar Orçamento</h3>
              <p className="text-foreground-500 text-sm leading-relaxed mb-5">Indicamos o modelo ideal para seu projeto e prazo de entrega.</p>
              <a href={whatsappUrl} target="_blank" rel="nofollow noreferrer" className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold text-sm px-6 py-3 rounded-full transition-all cursor-pointer whitespace-nowrap uppercase tracking-wide">
                <i className="ri-whatsapp-line" /> Falar no WhatsApp
              </a>
            </div>

            <div className="bg-white rounded-2xl border border-background-200/70 p-5">
              <h4 className="font-heading text-sm font-bold text-foreground-900 uppercase mb-3">Modelos Automáticos</h4>
              <div className="space-y-3">
                {automaticas.map((p) => (
                  <div key={p.id} className="flex items-start gap-3 py-3 border-b border-background-200/40 last:border-0">
                    <img alt={p.name} className="w-16 h-16 object-contain rounded-lg bg-background-100 flex-shrink-0" src={p.image} />
                    <div>
                      <p className="font-bold text-sm text-foreground-900">{p.name}</p>
                      <p className="text-foreground-400 text-xs">{p.specs.potencia} · {p.specs.velocidade}</p>
                      <p className="text-primary-600 text-xs font-bold mt-0.5">{p.specs.material}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}